// Callout Block - Color-coded info/warning/error/success boxes
import { Info, AlertTriangle, XCircle, CheckCircle, Lightbulb } from 'lucide-react';

class CalloutBlock {
  static get toolbox() {
    return {
      title: 'Callout',
      icon: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>'
    };
  }

  static get isReadOnlySupported() {
    return true;
  }

  constructor({ data, config, api, readOnly }) {
    this.data = {
      type: data.type || 'info',
      title: data.title || '',
      message: data.message || ''
    };
    this.config = config;
    this.api = api;
    this.readOnly = readOnly;
    this.wrapper = null;
  }

  render() {
    this.wrapper = document.createElement('div');
    this.wrapper.classList.add('callout-block');
    this._renderCallout();
    return this.wrapper;
  }

  _getTypeStyles(type) {
    const styles = {
      info: {
        bg: 'bg-blue-50 dark:bg-blue-900/20',
        border: 'border-blue-200 dark:border-blue-800',
        icon: 'text-blue-600 dark:text-blue-400',
        title: 'text-blue-800 dark:text-blue-200'
      },
      warning: {
        bg: 'bg-amber-50 dark:bg-amber-900/20',
        border: 'border-amber-200 dark:border-amber-800',
        icon: 'text-amber-600 dark:text-amber-400',
        title: 'text-amber-800 dark:text-amber-200'
      },
      error: {
        bg: 'bg-red-50 dark:bg-red-900/20',
        border: 'border-red-200 dark:border-red-800',
        icon: 'text-red-600 dark:text-red-400',
        title: 'text-red-800 dark:text-red-200'
      },
      success: {
        bg: 'bg-emerald-50 dark:bg-emerald-900/20',
        border: 'border-emerald-200 dark:border-emerald-800',
        icon: 'text-emerald-600 dark:text-emerald-400',
        title: 'text-emerald-800 dark:text-emerald-200'
      },
      note: {
        bg: 'bg-purple-50 dark:bg-purple-900/20',
        border: 'border-purple-200 dark:border-purple-800',
        icon: 'text-purple-600 dark:text-purple-400',
        title: 'text-purple-800 dark:text-purple-200'
      }
    };
    return styles[type] || styles.info;
  }

  _getIcon(type) {
    const icons = {
      info: '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>',
      warning: '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>',
      error: '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>',
      success: '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>',
      note: '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 18V5l12-2v13"></path><circle cx="6" cy="18" r="3"></circle><circle cx="18" cy="16" r="3"></circle></svg>'
    };
    return icons[type] || icons.info;
  }

  _renderCallout() {
    const styles = this._getTypeStyles(this.data.type);
    
    this.wrapper.innerHTML = `
      <div class="p-4 rounded-lg border ${styles.bg} ${styles.border}" data-testid="callout-block">
        <div class="flex items-start gap-3">
          <div class="flex-shrink-0 mt-0.5 ${styles.icon}">
            ${this._getIcon(this.data.type)}
          </div>
          <div class="flex-1 min-w-0">
            ${!this.readOnly ? `
              <select class="callout-type-select mb-2 text-xs px-2 py-1 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                <option value="info" ${this.data.type === 'info' ? 'selected' : ''}>Info</option>
                <option value="warning" ${this.data.type === 'warning' ? 'selected' : ''}>Warning</option>
                <option value="error" ${this.data.type === 'error' ? 'selected' : ''}>Error</option>
                <option value="success" ${this.data.type === 'success' ? 'selected' : ''}>Success</option>
                <option value="note" ${this.data.type === 'note' ? 'selected' : ''}>Note</option>
              </select>
            ` : ''}
            <div 
              class="callout-title font-semibold ${styles.title} mb-1"
              contenteditable="${!this.readOnly}"
              data-placeholder="Title (optional)"
            >${this.data.title}</div>
            <div 
              class="callout-message text-slate-700 dark:text-slate-300"
              contenteditable="${!this.readOnly}"
              data-placeholder="Enter callout message..."
            >${this.data.message}</div>
          </div>
        </div>
      </div>
    `;

    if (!this.readOnly) {
      // Type selector
      const select = this.wrapper.querySelector('.callout-type-select');
      if (select) {
        select.addEventListener('change', (e) => {
          this.data.type = e.target.value;
          this._renderCallout();
        });
      }

      // Title input
      const title = this.wrapper.querySelector('.callout-title');
      if (title) {
        title.addEventListener('input', (e) => {
          this.data.title = e.target.textContent;
        });
      }

      // Message input
      const message = this.wrapper.querySelector('.callout-message');
      if (message) {
        message.addEventListener('input', (e) => {
          this.data.message = e.target.innerHTML;
        });
      }
    }
  }

  save() {
    return {
      type: this.data.type,
      title: this.data.title,
      message: this.data.message
    };
  }

  static get sanitize() {
    return {
      title: {},
      message: {
        br: true,
        b: true,
        i: true,
        a: { href: true }
      }
    };
  }
}

export default CalloutBlock;
